import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { DashboardStats } from '../../services/dashboardService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Props {
  stats: DashboardStats | null;
}

export default function PriorityDistribution({ stats }: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation();

  if (!stats) {
    return (
      <div className={`rounded-lg shadow p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className={`h-6 w-40 rounded mb-4 animate-pulse ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}></div>
        <div className={`h-64 w-full rounded animate-pulse ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}></div>
      </div>
    );
  }

  const priorityData = stats.ticketsByPriority;
  const total = Object.values(priorityData).reduce((sum, count) => sum + count, 0);

  const data = {
    labels: [
      t('tickets.priority.low', 'Low'),
      t('tickets.priority.medium', 'Medium'),
      t('tickets.priority.high', 'High'),
      t('tickets.priority.urgent', 'Urgent'),
    ],
    datasets: [
      {
        label: t('pageHeaders.dashboard.ticketCount', 'Ticket Count'),
        data: [
          priorityData.low,
          priorityData.medium,
          priorityData.high,
          priorityData.urgent,
        ],
        backgroundColor: [
          theme === 'dark' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(34, 197, 94, 0.7)', // Green for low
          theme === 'dark' ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.7)', // Blue for medium
          theme === 'dark' ? 'rgba(245, 158, 11, 0.8)' : 'rgba(245, 158, 11, 0.7)', // Orange for high
          theme === 'dark' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(239, 68, 68, 0.7)', // Red for urgent
        ],
        borderColor: [
          theme === 'dark' ? '#22C55E' : '#16A34A',
          theme === 'dark' ? '#3B82F6' : '#2563EB',
          theme === 'dark' ? '#F59E0B' : '#D97706',
          theme === 'dark' ? '#EF4444' : '#DC2626',
        ],
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        titleColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        bodyColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        borderColor: theme === 'dark' ? '#374151' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
            return `${context.label}: ${value} tickets (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: theme === 'dark' ? '#374151' : '#F3F4F6',
          drawBorder: false,
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          callback: function(value: any) {
            return Math.floor(value).toString();
          },
        },
      },
    },
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'text-green-500';
      case 'medium': return 'text-blue-500';
      case 'high': return 'text-orange-500';
      case 'urgent': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'low':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'medium':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'high':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'urgent':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getPriorityBgColor = (priority: string) => {
    switch (priority) {
      case 'low': return theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50';
      case 'medium': return theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50';
      case 'high': return theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50';
      case 'urgent': return theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50';
      default: return theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50';
    }
  };

  // Calculate priority score (weighted average)
  const priorityScore = total > 0 
    ? ((priorityData.low * 1 + priorityData.medium * 2 + priorityData.high * 3 + priorityData.urgent * 4) / total).toFixed(1)
    : '0';

  const getScoreColor = (score: number) => {
    if (score <= 1.5) return 'text-green-500';
    if (score <= 2.5) return 'text-blue-500';
    if (score <= 3.5) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <div className={`rounded-lg shadow p-6 ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="mb-6">
        <h3 className={`text-lg font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {t('pageHeaders.dashboard.priorityDistribution', 'Priority Distribution')}
        </h3>
        <p className={`text-sm mt-1 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {t('pageHeaders.dashboard.priorityDescription', 'Breakdown of tickets by priority level')}
        </p>
      </div>

      {/* Priority Score */}
      <div className={`mb-6 p-4 rounded-lg ${
        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <div className={`text-sm font-medium ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('pageHeaders.dashboard.averagePriority', 'Average Priority Score')}
            </div>
            <div className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t('pageHeaders.dashboard.priorityScoreDesc', 'Weighted average (1=Low, 4=Urgent)')}
            </div>
          </div>
          <div className={`text-2xl font-bold ${getScoreColor(parseFloat(priorityScore))}`}>
            {priorityScore}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-48 mb-6">
        <Bar data={data} options={options} />
      </div>

      {/* Priority Breakdown */}
      <div className="space-y-3">
        {Object.entries(priorityData).map(([priority, count]) => {
          const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0';
          return (
            <div key={priority} className={`p-3 rounded-lg ${getPriorityBgColor(priority)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`${getPriorityColor(priority)}`}>
                    {getPriorityIcon(priority)}
                  </div>
                  <div>
                    <span className={`text-sm font-medium capitalize ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {t(`tickets.priority.${priority}`, priority)}
                    </span>
                    <div className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {percentage}% of total
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {count}
                  </div>
                  <div className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    tickets
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommendations */}
      {priorityData.urgent > 0 && (
        <div className={`mt-6 p-4 rounded-lg border-l-4 border-red-500 ${
          theme === 'dark' ? 'bg-red-900/10' : 'bg-red-50'
        }`}>
          <div className="flex items-start">
            <div className="text-red-500 mr-3 mt-0.5">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className={`text-sm font-medium text-red-600 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-600'
              }`}>
                {t('pageHeaders.dashboard.urgentAttention', 'Urgent Attention Required')}
              </h4>
              <p className={`text-xs mt-1 ${
                theme === 'dark' ? 'text-red-300' : 'text-red-700'
              }`}>
                {priorityData.urgent} {t('pageHeaders.dashboard.urgentTicketsMessage', 'urgent tickets require immediate attention')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
